import { VStack, HStack, Divider, Text, Icon, Box } from "@chakra-ui/react";
import Section from "../../Components/Section";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import KYDButton from "src/ui/KYDButton";
import { ChevronLeftIcon } from "@chakra-ui/icons";
import { Link, useSearchParams } from "react-router-dom";
import { FiSend } from "react-icons/fi";
import KYDCard from "src/ui/KYDCard";
import {
  KYDAdminFeed,
  KYDOrganizationFull,
} from "@common/interfaces/KYDOrganization";
import dayjs from "dayjs";
import {
  Event2ColData,
  KYDInternalCampaign,
} from "@common/interfaces/KYDCampaign";
import { Event1ColData } from "@common/interfaces/KYDCampaign";
import { EventListData } from "@common/interfaces/KYDCampaign";
import EventCard from "../../Components/EventCard";
import SendTestEmail from "../../Components/SendTestEmail";
import { KYDOrganizationUser } from "@common/interfaces/KYDUser";
import { KYDEventSummary } from "@common/interfaces/KYDEvent";

const ReviewCampaignForm = ({
  isMobile,
  values,
  organization,
  organizationUsers,
  loading,
  onSubmit,
}: {
  isMobile: boolean;
  values: KYDInternalCampaign;
  organization: KYDOrganizationFull;
  organizationUsers: KYDOrganizationUser[];
  loading: boolean;
  onSubmit: () => void;
}) => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const renderHeadline = (title: string) => {
    return (
      <Text
        fontSize="16px"
        fontWeight="600"
        textAlign="left"
        color="rgba(26, 32, 44, 0.75)"
        letterSpacing="-0.48px"
      >
        {title}
      </Text>
    );
  };

  const renderSubheadline = (title: string) => {
    return (
      <Text
        fontSize="14px"
        fontWeight="400"
        textAlign="left"
        color="rgba(34, 34, 34, 0.80)"
        letterSpacing="-0.42px"
        fontFamily="Work Sans"
        fontStyle="normal"
        lineHeight="140%"
      >
        {title}
      </Text>
    );
  };

  const renderInboxHeader = (text: string) => {
    return (
      <Text
        overflow="hidden"
        color="rgba(34, 34, 34, 0.80)"
        textOverflow="ellipsis"
        fontFamily="Work Sans"
        fontSize="12px"
        fontStyle="normal"
        fontWeight="600"
        lineHeight="100%"
        letterSpacing="-0.36px"
      >
        {text}
      </Text>
    );
  };

  const isSentInFuture =
    new Date(values.send_at).getTime() > new Date().getTime();

  const allEvents = values.blocks
    .filter((block) =>
      ["eventList", "event1col", "event2col"].includes(block.type)
    )
    .flatMap((block) => {
      switch (block.type) {
        case "eventList":
          return (block as EventListData).events.map((event) => ({
            ...event.event,
            image: event.event.image,
          }));
        case "event1col": {
          const event = (block as Event1ColData).event;
          return event
            ? [
                {
                  ...event.event,
                  image: event.custom_image || event.event.image,
                },
              ]
            : [];
        }
        case "event2col":
          return (block as Event2ColData).events.map((event) => ({
            ...event.event,
            image: event.custom_image || event.event.image,
          }));
        default:
          return [];
      }
    }) as unknown as
    | KYDEventSummary[]
    | KYDAdminFeed["external_events"][number][];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <VStack
        w={isMobile ? "100%" : "443px"}
        bg="white"
        h="100%"
        alignItems="flex-start"
        overflowY="auto"
        position="relative"
      >
        <HStack gap={6} w="100%" justifyContent="space-between">
          <KYDButton
            to={`/campaigns/create?id=${id}`}
            variant="outline"
            leftIcon={<Icon as={ChevronLeftIcon} />}
            px={"12px"}
          >
            Back
          </KYDButton>
          <KYDButton
            to="/campaigns/create/review"
            onClick={(e) => {
              e.preventDefault();
              onSubmit();
            }}
            isLoading={loading}
            isDisabled={loading}
            px={"12px"}
            leftIcon={<Icon as={FiSend} transform="rotate(45deg)" mr={1} />}
          >
            {isSentInFuture ? "Schedule" : "Send Email"}
          </KYDButton>
        </HStack>
        <Divider mt={6} />
        <Section
          title="Review Campaign"
          isDragable={false}
          alignItems="flex-start"
          justifyContent="flex-start"
        >
          {renderHeadline("Audience")}
          <HStack w="100%" spacing={4}>
            <KYDCard
              title="Sending to"
              value={`${values.size} ${
                values.size === 1 ? "contact" : "contacts"
              }`}
              flex="1"
            />
            <KYDCard
              title="Segment"
              value={values.segments[0]?.name}
              flex="2"
            />
          </HStack>
        </Section>

        <VStack w="100%" alignItems="flex-start">
          {renderHeadline("Time To Send")}
          {renderSubheadline(
            new Date(values.send_at).getTime() <= new Date().getTime()
              ? "Immediately"
              : "Schedule"
          )}
        </VStack>

        <VStack w="100%" alignItems="flex-start">
          {renderHeadline("Subject Line")}
          {renderSubheadline(values.subject)}
        </VStack>

        <VStack w="100%" alignItems="flex-start">
          {renderHeadline("Email Preheader")}
          {renderSubheadline(values.preheader)}
        </VStack>

        <VStack w="100%" alignItems="flex-start">
          {renderHeadline("Inbox Preview")}
          <Box
            position="relative"
            borderRadius="8px"
            border="1px solid rgba(0, 0, 0, 0.10)"
            background="#FFF"
            boxShadow="0px 2px 8px 0px rgba(0, 0, 0, 0.10)"
            display="flex"
            padding="8px 12px"
            flexDirection="column"
            justifyContent="center"
            alignItems="flex-start"
            gap="5px"
            alignSelf="stretch"
          >
            <HStack w="100%" justifyContent="space-between">
              {renderInboxHeader(`${organization.name} (via KYD)`)}
              {renderInboxHeader(
                new Date(values.send_at).getTime() <= new Date().getTime()
                  ? dayjs().format("MMM D")
                  : dayjs(values.send_at).format("MMM D")
              )}
            </HStack>
            {renderInboxHeader(values.subject)}
            {values.preheader && (
              <Text
                color="rgba(34, 34, 34, 0.60)"
                fontFamily="Work Sans"
                fontSize="12px"
                fontStyle="normal"
                fontWeight="400"
                lineHeight="100%"
                letterSpacing="-0.36px"
              >
                {values.preheader}
              </Text>
            )}
          </Box>
        </VStack>
        <VStack mt={6} w="100%" alignItems="flex-start">
          {renderHeadline("Included Events")}
          {allEvents.map(
            (
              event: KYDEventSummary | KYDAdminFeed["external_events"][number],
              index: number
            ) => (
              <EventCard
                key={`${event.id}-${index}`}
                event={event}
                isExternal={!("venue" in event)}
              />
            )
          )}
        </VStack>
        <SendTestEmail
          organizationUsers={organizationUsers}
          campaignId={values.id}
        />
      </VStack>
    </LocalizationProvider>
  );
};

export default ReviewCampaignForm;
